class CollectionMain extends HTMLElement {
  constructor() {
    super();

    this.filtersOpen = this.dataset.filtersOpen === "true";
    this.filtersMobileOpen = this.dataset.filtersMobileOpen === "true";
    this.ajaxContent = this.querySelector("[data-ajax-content]");
    this.debounceTimer = null;
    this.debounceDelay = 750;
    this.filtersChanged = false;
  }

  toggleFilters() {
    const toggleFiltersButton = this.querySelector("[data-toggle-filters]");
    if (!toggleFiltersButton) return;

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // Mobile: toggle mobile filters
      this.filtersMobileOpen = !this.filtersMobileOpen;
      this.dataset.filtersMobileOpen = this.filtersMobileOpen;

      if (this.filtersMobileOpen) {
        toggleFiltersButton.setAttribute("aria-expanded", "true");
        // Prevent body scroll when mobile filters are open
        document.body.style.overflow = "hidden";
      } else {
        toggleFiltersButton.setAttribute("aria-expanded", "false");
        // Restore body scroll when mobile filters are closed
        document.body.style.overflow = "";
      }
    } else {
      // Desktop: toggle desktop filters
      this.filtersOpen = !this.filtersOpen;
      this.dataset.filtersOpen = this.filtersOpen;

      if (this.filtersOpen) {
        toggleFiltersButton.setAttribute("aria-expanded", "true");
      } else {
        toggleFiltersButton.setAttribute("aria-expanded", "false");
      }
    }
  }

  closeFilters() {
    const toggleFiltersButton = this.querySelector("[data-toggle-filters]");
    if (!toggleFiltersButton) return;

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // Close mobile filters
      this.filtersMobileOpen = false;
      this.dataset.filtersMobileOpen = this.filtersMobileOpen;
      document.body.style.overflow = "";
    } else {
      // Close desktop filters
      this.filtersOpen = false;
      this.dataset.filtersOpen = this.filtersOpen;
    }

    toggleFiltersButton.setAttribute("aria-expanded", "false");
  }

  setLoading(loading) {
    this.toggleAttribute("data-loading", loading);
  }

  markFiltersChanged() {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;

    this.filtersChanged = true;
    this.updateApplyButton();
  }

  updateClearButtonVisibility(filterGroup) {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return; // Desktop keeps server-side visibility

    const clearButton = filterGroup.querySelector("[data-clear-filter]");
    if (!clearButton) return;

    let hasActiveFilters = false;

    // Check for range slider first (price or custom range)
    const rangeSlider = filterGroup.querySelector("[data-range-slider]");
    const priceRange = filterGroup.querySelector("[data-price-range]");
    const hasSlider = rangeSlider || priceRange;

    if (hasSlider) {
      // For sliders, check if values differ from defaults
      const priceInputs = filterGroup.querySelectorAll("[data-input-min], [data-input-max]");
      priceInputs.forEach(input => {
        if (input.value && input.value.trim() !== "") {
          hasActiveFilters = true;
        }
      });

      if (rangeSlider) {
        const sliderMin = rangeSlider.querySelector("[data-slider-min]");
        const sliderMax = rangeSlider.querySelector("[data-slider-max]");
        const rangeMin = parseFloat(rangeSlider.dataset.min);
        const rangeMax = parseFloat(rangeSlider.dataset.max);

        if (sliderMin && sliderMax) {
          const currentMin = parseFloat(sliderMin.value);
          const currentMax = parseFloat(sliderMax.value);

          if (currentMin !== rangeMin || currentMax !== rangeMax) {
            hasActiveFilters = true;
          }
        }
      }
    } else {
      // For regular filters, check regular checkboxes
      const checkboxes = filterGroup.querySelectorAll('input[type="checkbox"]:checked');
      if (checkboxes.length > 0) {
        hasActiveFilters = true;
      }
    }

    if (hasActiveFilters) {
      clearButton.classList.remove("collection-filters__group-clear--hidden");
    } else {
      clearButton.classList.add("collection-filters__group-clear--hidden");
    }
  }

  updateFilterGroupCount(filterGroup) {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;

    const summary = filterGroup.querySelector(".collection-filters__group-summary");
    if (!summary) return;

    const title = summary.querySelector(".collection-filters__group-title");
    if (!title) return;

    let activeCount = 0;

    // Check for range slider first (price or custom range)
    const rangeSlider = filterGroup.querySelector("[data-range-slider]");
    const priceRange = filterGroup.querySelector("[data-price-range]");
    const hasSlider = rangeSlider || priceRange;

    if (hasSlider) {
      // For sliders, check if values differ from defaults
      let hasRangeValue = false;

      const priceInputs = filterGroup.querySelectorAll("[data-input-min], [data-input-max]");
      priceInputs.forEach(input => {
        if (input.value && input.value.trim() !== "") {
          hasRangeValue = true;
        }
      });

      if (rangeSlider) {
        const sliderMin = rangeSlider.querySelector("[data-slider-min]");
        const sliderMax = rangeSlider.querySelector("[data-slider-max]");
        const rangeMin = parseFloat(rangeSlider.dataset.min);
        const rangeMax = parseFloat(rangeSlider.dataset.max);

        if (sliderMin && sliderMax) {
          const currentMin = parseFloat(sliderMin.value);
          const currentMax = parseFloat(sliderMax.value);

          if (currentMin !== rangeMin || currentMax !== rangeMax) {
            hasRangeValue = true;
          }
        }
      }

      if (hasRangeValue) {
        activeCount = 1;
      }
    } else {
      // For regular filters, count checked checkboxes
      const checkboxes = filterGroup.querySelectorAll('input[type="checkbox"]:checked');
      activeCount = checkboxes.length;
    }

    // Update or create count span
    let countSpan = title.querySelector(".collection-filters__group-count");

    if (activeCount > 0) {
      if (!countSpan) {
        countSpan = document.createElement("span");
        countSpan.className = "collection-filters__group-count";
        title.appendChild(countSpan);
      }
      countSpan.textContent = `(${activeCount})`;
    } else {
      if (countSpan) {
        countSpan.remove();
      }
    }
  }

  updateApplyButton() {
    const applyButton = this.querySelector("[data-apply-filters]");
    if (!applyButton) return;

    const defaultText = applyButton.dataset.textDefault;
    const changedText = applyButton.dataset.textExpanded;
    const span = applyButton.querySelector("span");

    if (!defaultText || !changedText || !span) return;

    if (this.filtersChanged) {
      span.textContent = changedText;
      applyButton.classList.add("is-changed");
    } else {
      span.textContent = defaultText;
      applyButton.classList.remove("is-changed");
    }
  }

  refreshListing(url) {
    this.setLoading(true);
    fetch(url)
      .then(response => response.text())
      .then(data => {
        const resultsMarkup = new DOMParser().parseFromString(data, "text/html").querySelector("[data-ajax-content]").innerHTML;
        this.ajaxContent.innerHTML = resultsMarkup;

        this.filtersChanged = false;
        this.updateApplyButton();

        const isMobile = window.innerWidth < 768;
        if (isMobile && this.filtersMobileOpen) {
          this.closeFilters();
        }

        this.setLoading(false);
        window.history.pushState({}, "", url);

        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
        this.scrollIntoView({
          behavior: "smooth",
          block: "start",
          offset: { top: headerHeight }
        });

        this.recurringEvents();
      })
      .catch(error => {
        console.error("Error refreshing listing:", error);
        this.setLoading(false);
      });
  }

  initRangeSliders() {
    const rangeSliders = this.querySelectorAll("[data-range-slider]");

    rangeSliders.forEach(rangeSlider => {
      const sliderMin = rangeSlider.querySelector("[data-slider-min]");
      const sliderMax = rangeSlider.querySelector("[data-slider-max]");
      const inputMin = rangeSlider.querySelector("[data-input-min]");
      const inputMax = rangeSlider.querySelector("[data-input-max]");
      const sliderRange = rangeSlider.querySelector("[data-slider-range]");
      const checkboxes = rangeSlider.querySelectorAll("[data-range-checkbox]");

      // Safety check - skip if essential elements are missing
      if (!sliderMin || !sliderMax || !inputMin || !inputMax || !sliderRange) return;

      const rangeMin = parseFloat(rangeSlider.dataset.min);
      const rangeMax = parseFloat(rangeSlider.dataset.max);

      // Safety check - skip if range is invalid
      if (isNaN(rangeMin) || isNaN(rangeMax) || rangeMin >= rangeMax) return;

      const updateCheckboxes = () => {
        const minVal = parseFloat(sliderMin.value);
        const maxVal = parseFloat(sliderMax.value);

        checkboxes.forEach(checkbox => {
          const value = parseFloat(checkbox.dataset.rangeValue);
          checkbox.checked = value >= minVal && value <= maxVal;
        });
      };

      const updateSliderRange = () => {
        const minVal = parseFloat(sliderMin.value);
        const maxVal = parseFloat(sliderMax.value);

        const minPercent = ((minVal - rangeMin) / (rangeMax - rangeMin)) * 100;
        const maxPercent = ((maxVal - rangeMin) / (rangeMax - rangeMin)) * 100;

        sliderRange.style.left = `${minPercent}%`;
        sliderRange.style.right = `${100 - maxPercent}%`;
      };

      sliderMin.addEventListener("input", () => {
        const minVal = parseFloat(sliderMin.value);
        const maxVal = parseFloat(sliderMax.value);

        if (minVal >= maxVal) {
          sliderMin.value = maxVal - parseFloat(sliderMin.step);
        }

        inputMin.value = sliderMin.value;
        updateSliderRange();
        updateCheckboxes();
      });

      sliderMax.addEventListener("input", () => {
        const minVal = parseFloat(sliderMin.value);
        const maxVal = parseFloat(sliderMax.value);

        if (maxVal <= minVal) {
          sliderMax.value = minVal + parseFloat(sliderMax.step);
        }

        inputMax.value = sliderMax.value;
        updateSliderRange();
        updateCheckboxes();
      });

      inputMin.addEventListener("input", () => {
        const minVal = parseFloat(inputMin.value) || rangeMin;
        const maxVal = parseFloat(inputMax.value) || rangeMax;

        if (minVal >= maxVal) {
          inputMin.value = maxVal - parseFloat(sliderMin.step);
        }

        sliderMin.value = inputMin.value;
        updateSliderRange();
        updateCheckboxes();
      });

      inputMax.addEventListener("input", () => {
        const minVal = parseFloat(inputMin.value) || rangeMin;
        const maxVal = parseFloat(inputMax.value) || rangeMax;

        if (maxVal <= minVal) {
          inputMax.value = minVal + parseFloat(sliderMax.step);
        }

        sliderMax.value = inputMax.value;
        updateSliderRange();
        updateCheckboxes();
      });

      sliderMin.addEventListener("change", () => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          const filterGroup = sliderMin.closest(".collection-filters__group");
          if (filterGroup) {
            this.updateClearButtonVisibility(filterGroup);
            this.updateFilterGroupCount(filterGroup);
          }
          this.markFiltersChanged();
        } else {
          this.applyFilters(true);
        }
      });

      sliderMax.addEventListener("change", () => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          const filterGroup = sliderMax.closest(".collection-filters__group");
          if (filterGroup) {
            this.updateClearButtonVisibility(filterGroup);
            this.updateFilterGroupCount(filterGroup);
          }
          this.markFiltersChanged();
        } else {
          this.applyFilters(true);
        }
      });

      updateSliderRange();
    });
  }

  initPriceSliders() {
    const priceRanges = this.querySelectorAll("[data-price-range]");

    priceRanges.forEach(priceRange => {
      const sliderMin = priceRange.querySelector("[data-slider-min]");
      const sliderMax = priceRange.querySelector("[data-slider-max]");
      const inputMin = priceRange.querySelector("[data-input-min]");
      const inputMax = priceRange.querySelector("[data-input-max]");
      const sliderRange = priceRange.querySelector("[data-slider-range]");

      const rangeMin = parseFloat(priceRange.dataset.min);
      const rangeMax = parseFloat(priceRange.dataset.max);

      const updateSliderRange = () => {
        const minVal = parseFloat(sliderMin.value);
        const maxVal = parseFloat(sliderMax.value);

        const minPercent = ((minVal - rangeMin) / (rangeMax - rangeMin)) * 100;
        const maxPercent = ((maxVal - rangeMin) / (rangeMax - rangeMin)) * 100;

        sliderRange.style.left = `${minPercent}%`;
        sliderRange.style.right = `${100 - maxPercent}%`;
      };

      sliderMin.addEventListener("input", () => {
        const minVal = parseFloat(sliderMin.value);
        const maxVal = parseFloat(sliderMax.value);

        if (minVal >= maxVal) {
          sliderMin.value = maxVal - 1;
        }

        inputMin.value = sliderMin.value;
        updateSliderRange();
      });

      sliderMax.addEventListener("input", () => {
        const minVal = parseFloat(sliderMin.value);
        const maxVal = parseFloat(sliderMax.value);

        if (maxVal <= minVal) {
          sliderMax.value = minVal + 1;
        }

        inputMax.value = sliderMax.value;
        updateSliderRange();
      });

      inputMin.addEventListener("input", () => {
        const minVal = parseFloat(inputMin.value) || rangeMin;
        const maxVal = parseFloat(inputMax.value) || rangeMax;

        if (minVal >= maxVal) {
          inputMin.value = maxVal - 1;
        }

        sliderMin.value = inputMin.value;
        updateSliderRange();
      });

      inputMax.addEventListener("input", () => {
        const minVal = parseFloat(inputMin.value) || rangeMin;
        const maxVal = parseFloat(inputMax.value) || rangeMax;

        if (maxVal <= minVal) {
          inputMax.value = minVal + 1;
        }

        sliderMax.value = inputMax.value;
        updateSliderRange();
      });

      sliderMin.addEventListener("change", () => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          const filterGroup = sliderMin.closest(".collection-filters__group");
          if (filterGroup) {
            this.updateClearButtonVisibility(filterGroup);
            this.updateFilterGroupCount(filterGroup);
          }
          this.markFiltersChanged();
        } else {
          this.applyFilters(true);
        }
      });

      sliderMax.addEventListener("change", () => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          const filterGroup = sliderMax.closest(".collection-filters__group");
          if (filterGroup) {
            this.updateClearButtonVisibility(filterGroup);
            this.updateFilterGroupCount(filterGroup);
          }
          this.markFiltersChanged();
        } else {
          this.applyFilters(true);
        }
      });

      updateSliderRange();
    });
  }

  recurringEvents() {
    const toggleFiltersButton = this.querySelector("[data-toggle-filters]");
    if (toggleFiltersButton && toggleFiltersButton.dataset.listenerAttached !== "true") {
      toggleFiltersButton.dataset.listenerAttached = "true";
      toggleFiltersButton.addEventListener("click", () => {
        this.toggleFilters();
      });
    }

    const paginationLinks = this.querySelectorAll("[data-pagination] a");
    if (paginationLinks.length > 0) {
      paginationLinks.forEach(link => {
        if (link.dataset.listenerAttached === "true") return;
        link.dataset.listenerAttached = "true";

        link.addEventListener("click", e => {
          e.preventDefault();
          this.refreshListing(link.href);
        });
      });
    }

    const filterForm = this.querySelector("[data-filter-form]");
    if (filterForm) {
      // Skip if already initialized
      if (filterForm.dataset.eventsAttached !== "true") {
        filterForm.dataset.eventsAttached = "true";

        const filterInputs = filterForm.querySelectorAll('input[type="checkbox"]:not([data-range-checkbox])');
        filterInputs.forEach(input => {
          input.addEventListener("change", () => {
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
              const filterGroup = input.closest(".collection-filters__group");
              if (filterGroup) {
                this.updateClearButtonVisibility(filterGroup);
                this.updateFilterGroupCount(filterGroup);
              }
              this.markFiltersChanged();
            } else {
              this.applyFilters(false);
            }
          });
        });

        const priceInputs = filterForm.querySelectorAll("[data-input-min], [data-input-max]");
        priceInputs.forEach(input => {
          input.addEventListener("change", () => {
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
              const filterGroup = input.closest(".collection-filters__group");
              if (filterGroup) {
                this.updateClearButtonVisibility(filterGroup);
                this.updateFilterGroupCount(filterGroup);
              }
              this.markFiltersChanged();
            } else {
              this.applyFilters(false);
            }
          });
        });
      }
    }

    const clearFilterLinks = this.querySelectorAll("[data-clear-filter]");
    if (clearFilterLinks.length > 0) {
      clearFilterLinks.forEach(link => {
        // Skip if already initialized
        if (link.dataset.listenerAttached === "true") return;
        link.dataset.listenerAttached = "true";

        link.addEventListener("click", e => {
          e.preventDefault();
          e.stopPropagation();

          const isMobile = window.innerWidth < 768;

          if (isMobile) {
            const filterGroup = link.closest(".collection-filters__group");
            if (filterGroup) {
              const checkboxes = filterGroup.querySelectorAll('input[type="checkbox"]');
              checkboxes.forEach(checkbox => {
                if (checkbox.checked) {
                  checkbox.checked = false;
                }
              });

              const priceInputs = filterGroup.querySelectorAll("[data-input-min], [data-input-max]");
              priceInputs.forEach(input => {
                input.value = "";
              });

              const sliderMin = filterGroup.querySelector("[data-slider-min]");
              const sliderMax = filterGroup.querySelector("[data-slider-max]");
              if (sliderMin && sliderMax) {
                const priceRange = filterGroup.querySelector("[data-price-range]");
                const rangeSlider = filterGroup.querySelector("[data-range-slider]");
                const container = priceRange || rangeSlider;

                if (container) {
                  sliderMin.value = container.dataset.min;
                  sliderMax.value = container.dataset.max;

                  const sliderRange = filterGroup.querySelector("[data-slider-range]");
                  if (sliderRange) {
                    sliderRange.style.left = "0%";
                    sliderRange.style.right = "0%";
                  }

                  // Update checkboxes for range sliders
                  if (rangeSlider) {
                    const checkboxes = rangeSlider.querySelectorAll("[data-range-checkbox]");
                    checkboxes.forEach(checkbox => {
                      checkbox.checked = true;
                    });
                  }
                }
              }

              this.updateClearButtonVisibility(filterGroup);
              this.updateFilterGroupCount(filterGroup);
              this.markFiltersChanged();
            }
          } else {
            this.refreshListing(link.href);
          }
        });
      });
    }

    const clearAllButton = this.querySelectorAll("[data-clear-all-filters]");
    if (clearAllButton.length > 0) {
      clearAllButton.forEach(button => {
        if (button.dataset.listenerAttached === "true") return;
        button.dataset.listenerAttached = "true";

        button.addEventListener("click", e => {
          e.preventDefault();
          this.refreshListing(button.href);
        });
      });
    }

    const sortLinks = this.querySelectorAll("[data-sort-option]");
    if (sortLinks.length > 0) {
      sortLinks.forEach(link => {
        if (link.dataset.listenerAttached === "true") return;
        link.dataset.listenerAttached = "true";

        link.addEventListener("click", e => {
          e.preventDefault();
          this.refreshListing(link.href);

          const sortDetails = this.querySelector(".collection-main__sort-details");
          if (sortDetails) {
            sortDetails.removeAttribute("open");
          }
        });
      });
    }

    const isMobileInit = window.innerWidth < 768;
    if (isMobileInit) {
      const filterGroups = this.querySelectorAll(".collection-filters__group");
      filterGroups.forEach(group => {
        this.updateClearButtonVisibility(group);
        this.updateFilterGroupCount(group);
      });
    }

    this.initPriceSliders();
    this.initRangeSliders();

    const closeFiltersButton = this.querySelector("[data-close-filters]");
    if (closeFiltersButton) {
      closeFiltersButton.addEventListener("click", () => {
        this.closeFilters();
      });
    }

    const applyFiltersButton = this.querySelector("[data-apply-filters]");
    if (applyFiltersButton) {
      applyFiltersButton.addEventListener("click", () => {
        if (this.filtersChanged) {
          this.applyFilters(true);
        } else {
          this.closeFilters();
        }
      });
    }
  }

  applyFilters(immediate = false) {
    const filterForm = this.querySelector("[data-filter-form]");
    if (!filterForm) return;

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    const executeFilter = () => {
      const formData = new FormData(filterForm);
      const searchParams = new URLSearchParams(formData);
      const url = `${window.location.pathname}?${searchParams.toString()}`;
      this.refreshListing(url);
    };

    if (immediate) {
      executeFilter();
    } else {
      this.debounceTimer = setTimeout(() => {
        executeFilter();
        this.debounceTimer = null;
      }, this.debounceDelay);
    }
  }

  eventListeners() {
    this.recurringEvents();

    document.addEventListener("click", e => {
      const sortDetails = this.querySelector(".collection-main__sort-details");
      if (sortDetails && !e.target.closest(".collection-main__sort-details")) {
        sortDetails.removeAttribute("open");
      }
    });
  }

  init() {
    this.updateApplyButton();

    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      const filterGroups = this.querySelectorAll(".collection-filters__group");
      filterGroups.forEach(group => {
        this.updateClearButtonVisibility(group);
        this.updateFilterGroupCount(group);
      });
    }

    this.eventListeners();
  }

  connectedCallback() {
    this.init();
  }
}

if (!customElements.get("collection-main")) customElements.define("collection-main", CollectionMain);
